import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Modal,
} from "react-native";
import { FontAwesome6 } from "@expo/vector-icons";
import type {
  UnavailableWarning,
  DriverUnavailableChoice,
} from "../models/charger-model";

interface UnavailableWarningBannerProps {
  warning: UnavailableWarning;
  pausedForUnavailable: boolean;
  resumesAt: string | null;
  currentChoice: DriverUnavailableChoice;
  onChoiceMade: (choice: "pause_and_resume" | "end_charge") => void;
}

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

/**
 * Shown on the ChargingScreen when an unavailable window is approaching
 * or the charge is currently paused. Lets the driver choose between
 * pausing (auto-resume) or ending the charge.
 */
const UnavailableWarningBanner: React.FC<UnavailableWarningBannerProps> = ({
  warning,
  pausedForUnavailable,
  resumesAt,
  currentChoice,
  onChoiceMade,
}) => {
  const [modalVisible, setModalVisible] = useState(false);

  // ── Paused state ─────────────────────────────────────────────────────
  if (pausedForUnavailable) {
    return (
      <View style={[styles.banner, styles.bannerPaused]}>
        <FontAwesome6 name="circle-pause" size={18} color="#FCD34D" />
        <View style={styles.bannerTextWrap}>
          <Text style={styles.bannerTitle}>Charge Paused</Text>
          <Text style={styles.bannerBody}>
            Your charger is in an unavailable period.
            {resumesAt
              ? ` Charging will resume at ${formatTime(resumesAt)}.`
              : " It will resume when the window ends."}
          </Text>
          <Text style={styles.bannerHint}>
            Leave your vehicle plugged in.
          </Text>
        </View>
      </View>
    );
  }

  // ── Approaching state ────────────────────────────────────────────────
  if (!warning.approaching) return null;

  const urgent = warning.minutesUntil <= 5;

  return (
    <>
      <Pressable
        style={[styles.banner, urgent ? styles.bannerUrgent : styles.bannerWarn]}
        onPress={() => setModalVisible(true)}
      >
        <FontAwesome6
          name={urgent ? "triangle-exclamation" : "clock"}
          size={18}
          color={urgent ? "#FEF08A" : "#FCD34D"}
        />
        <View style={styles.bannerTextWrap}>
          <Text style={styles.bannerTitle}>
            Unavailable in {warning.minutesUntil} min
          </Text>
          <Text style={styles.bannerBody}>
            {currentChoice === "end_charge"
              ? "Your charge will end automatically."
              : currentChoice === "pause_and_resume"
                ? `Your charge will pause and resume at ${formatTime(warning.availableAgain)}.`
                : "Tap to choose: pause & resume, or end your charge."}
          </Text>
        </View>
        <FontAwesome6 name="chevron-right" size={12} color="#9CA3AF" />
      </Pressable>

      {/* Choice modal */}
      <Modal
        visible={modalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.overlay}>
          <View style={styles.modal}>
            <Text style={styles.modalTitle}>Unavailable Period Approaching</Text>
            <Text style={styles.modalSubtitle}>
              This charger becomes unavailable at{" "}
              {formatTime(warning.unavailableAt)} and resumes at{" "}
              {formatTime(warning.availableAgain)}.
            </Text>
            <Text style={styles.modalQuestion}>
              What would you like to do with your charge?
            </Text>

            {/* Option: Pause & Resume */}
            <Pressable
              style={[
                styles.optionCard,
                currentChoice === "pause_and_resume" && styles.optionCardSelected,
              ]}
              onPress={() => {
                onChoiceMade("pause_and_resume");
                setModalVisible(false);
              }}
            >
              <View style={styles.optionIcon}>
                <FontAwesome6 name="circle-pause" size={22} color="#51B868" />
              </View>
              <View style={styles.optionText}>
                <Text style={styles.optionTitle}>Pause & Auto-Resume</Text>
                <Text style={styles.optionDesc}>
                  Charging pauses during the unavailable window and resumes
                  automatically. Leave your vehicle plugged in. You won't be
                  billed while paused.
                </Text>
              </View>
              {currentChoice === "pause_and_resume" && (
                <FontAwesome6 name="check" size={16} color="#51B868" />
              )}
            </Pressable>

            {/* Option: End Charge */}
            <Pressable
              style={[
                styles.optionCard,
                currentChoice === "end_charge" && styles.optionCardSelected,
              ]}
              onPress={() => {
                onChoiceMade("end_charge");
                setModalVisible(false);
              }}
            >
              <View style={styles.optionIcon}>
                <FontAwesome6 name="circle-stop" size={22} color="#F97777" />
              </View>
              <View style={styles.optionText}>
                <Text style={styles.optionTitle}>End Charge</Text>
                <Text style={styles.optionDesc}>
                  Your charge will end when the unavailable period begins. You'll
                  be billed for usage up to that point.
                </Text>
              </View>
              {currentChoice === "end_charge" && (
                <FontAwesome6 name="check" size={16} color="#F97777" />
              )}
            </Pressable>

            <Pressable
              style={styles.dismissButton}
              onPress={() => setModalVisible(false)}
            >
              <Text style={styles.dismissText}>Close</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </>
  );
};

export default UnavailableWarningBanner;

const styles = StyleSheet.create({
  // ── Banner ──────────────────────────────────────────────────────────
  banner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginHorizontal: 16,
    marginVertical: 8,
    padding: 14,
    borderRadius: 12,
  },
  bannerWarn: {
    backgroundColor: "#422006",
    borderColor: "#92400E",
    borderWidth: 1,
  },
  bannerUrgent: {
    backgroundColor: "#7C2D12",
    borderColor: "#C2410C",
    borderWidth: 1,
  },
  bannerPaused: {
    backgroundColor: "#1E3A5F",
    borderColor: "#2563EB",
    borderWidth: 1,
  },
  bannerTextWrap: {
    flex: 1,
  },
  bannerTitle: {
    color: "#FCD34D",
    fontSize: 14,
    fontWeight: "700",
  },
  bannerBody: {
    color: "#D1D5DB",
    fontSize: 12,
    marginTop: 2,
    lineHeight: 17,
  },
  bannerHint: {
    color: "#93C5FD",
    fontSize: 11,
    fontWeight: "600",
    marginTop: 4,
  },

  // ── Modal ───────────────────────────────────────────────────────────
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.7)",
    justifyContent: "flex-end",
  },
  modal: {
    backgroundColor: "#0F2624",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 24,
    paddingBottom: 40,
  },
  modalTitle: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 6,
  },
  modalSubtitle: {
    color: "#9CA3AF",
    fontSize: 13,
    lineHeight: 19,
    marginBottom: 16,
  },
  modalQuestion: {
    color: "#D1D5DB",
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 14,
  },

  optionCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    backgroundColor: "#152C2B",
    borderColor: "#3A4A49",
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  optionCardSelected: {
    borderColor: "#51B868",
    backgroundColor: "#1A3F3D",
  },
  optionIcon: {
    width: 36,
    alignItems: "center",
  },
  optionText: {
    flex: 1,
  },
  optionTitle: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "700",
    marginBottom: 4,
  },
  optionDesc: {
    color: "#9CA3AF",
    fontSize: 12,
    lineHeight: 17,
  },

  dismissButton: {
    alignItems: "center",
    paddingVertical: 12,
    marginTop: 4,
  },
  dismissText: {
    color: "#9CA3AF",
    fontSize: 14,
    fontWeight: "600",
  },
});
